import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { provideTranslocoScope } from '@ngneat/transloco';
import { isPaymentMethod, MARKETPLACE_MATOMO_EVENT_CATEGORY_PAYMENT, PaymentMethod, PaymentMethodInfo } from '@platform-frontend/marketplace';
import { MobileService, TooltipDirective, TooltipService } from '@platform-frontend/ui-tools';
import { MatomoTracker } from 'ngx-matomo-client';
import { CHECKOUT_TRANSLOCO_SCOPE } from '../../checkout-transloco-scope';

@Component({
  selector: 'yat-payment-method-panel',
  templateUrl: './payment-method-panel.component.html',
  styleUrls: ['./payment-method-panel.component.scss'],
  providers: [provideTranslocoScope(CHECKOUT_TRANSLOCO_SCOPE)]
})
export class PaymentMethodPanelComponent implements OnChanges {
  constructor(
    private element: ElementRef,
    private mobileService: MobileService,
    private matomoTracker: MatomoTracker,
    private readonly tooltipService: TooltipService
  ) {}
  @Input()
  id: string;

  @Input()
  paymentMethod: PaymentMethod | PaymentMethodInfo;

  @Input()
  minified: boolean = false;

  @Input()
  defaultIcon: 'selected' | 'settings' | null = null;

  @Input()
  showDeleteIcon: boolean = true;

  @Input()
  showEditIcon: boolean = true;

  @Input()
  liveLicense: boolean = true;

  @Input()
  samePaymentMethodSelected: boolean = false;

  @Input()
  matomoEventClickAction: string | undefined;

  @Output()
  modalClosed: EventEmitter<void> = new EventEmitter();

  @Output()
  selectMethod: EventEmitter<() => void> = new EventEmitter();

  @Output()
  editMethod: EventEmitter<void> = new EventEmitter();

  @Output()
  prepareDeleteMethod: EventEmitter<void> = new EventEmitter();

  @Output()
  cancelDeleteMethod: EventEmitter<void> = new EventEmitter();

  @Output()
  deleteMethod: EventEmitter<() => void> = new EventEmitter();

  deleteMode: 'default' | 'delete' | 'deleteInProgress' = 'default';
  selectMode: 'default' | 'selectInProgress' = 'default';
  showSecondConfirmDialogFlag: boolean = false;
  spinnerShown: boolean = false;

  readonly MATOMO_EVENT_CATEGORY_PAYMENT = MARKETPLACE_MATOMO_EVENT_CATEGORY_PAYMENT;

  @ViewChild(TooltipDirective)
  tooltip: TooltipDirective;

  @ViewChild('icon')
  icon: ElementRef<HTMLImageElement>;

  private preSelectEvent: Event | null = null;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.deleteMode === 'delete' && !this.element.nativeElement.contains(event.target)) {
      this.abortConfirmDelete();
    }
  }

  showSecondConfirmDialog(): void {
    this.showSecondConfirmDialogFlag = true;
  }

  deleteItem(): void {
    this.delete();
    this.abortConfirmDelete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.samePaymentMethodSelected) {
      if (this.samePaymentMethodSelected && this.isMobile() && this.liveLicense) {
        this.updateComponent();
      }
    }
  }

  updateComponent(): void {
    this.settingsClicked();
  }

  settingsClicked(event?: Event): void {
    event?.stopPropagation();
    (this.liveLicense && this.showDeleteIcon) || this.showEditIcon ? (this.deleteMode = 'delete') : (this.deleteMode = 'default');
  }

  selectPayment(event: Event): void {
    if (this.isMobile() && this.preSelectEvent !== null) {
      this.preSelectEvent = event;
    } else {
      this.select(event);
    }
  }

  select(event: Event): void {
    const effectiveEvent = this.preSelectEvent ? this.preSelectEvent : event;
    this.preSelectEvent = null;

    const target = effectiveEvent.target as HTMLElement;
    if (!target.matches('.no-select, .no-select *')) {
      if (this.defaultIcon === null) {
        this.selectMode = 'selectInProgress';
      }
      this.selectMethod.emit(() => this.selectCallback());
    }
  }

  isMobile(): boolean {
    return this.mobileService.isMobileScreenSize();
  }

  private selectCallback(): void {
    this.selectMode = 'default';
    // workaround to prevent element to still have focus after error
    this.element.nativeElement.ownerDocument.activeElement.blur();
  }

  edit(): void {
    this.editMethod.emit();
  }

  prepareDelete(): void {
    this.deleteMode = 'delete';
    this.prepareDeleteMethod.next();
  }

  delete(completeCallback?: () => void): void {
    this.showSecondConfirmDialogFlag = false;
    this.spinnerShown = true;
    this.deleteMode = 'deleteInProgress';
    this.deleteMethod.next(() => {
      this.completeDelete(completeCallback);
      this.spinnerShown = false;
    });
    this.matomoTracker.trackEvent(this.MATOMO_EVENT_CATEGORY_PAYMENT, 'Confim_DeletePaymentMethod', this.paymentMethod.type);
  }

  private completeDelete(completeCallback?: () => void): void {
    if (completeCallback) completeCallback();
    this.deleteMode = 'deleteInProgress';
    this.modalClosed.emit();
  }

  abortConfirmDelete(event?: Event): void {
    event?.stopPropagation();
    this.showSecondConfirmDialogFlag = false;
    this.completeDelete();
    this.deleteMode = 'default';
    this.cancelDeleteMethod.next();
    this.matomoTracker.trackEvent(this.MATOMO_EVENT_CATEGORY_PAYMENT, 'Cancel_DeletePaymentMethod', this.paymentMethod.type);
  }

  @HostListener('touchstart', ['$event'])
  toggleTooltip(event: TouchEvent): void {
    const target = event.target as HTMLElement;
    if (target.closest('.refused-payment')) {
      event.preventDefault();
      if (!this.element.nativeElement.contains(event.target) || this.tooltipService.isActive(this.tooltip)) {
        this.closeTooltip();
      } else {
        this.openTooltip();
      }
    }
  }

  openTooltip(): void {
    this.tooltipService.registerTooltip(this.tooltip);
  }

  closeTooltip(): void {
    this.tooltipService.deregisterTooltip(this.tooltip);
  }

  isPaymentMethod(paymentMethod: PaymentMethod | PaymentMethodInfo): paymentMethod is PaymentMethod {
    return isPaymentMethod(paymentMethod);
  }
}
